import { computed, ref, toValue, watch } from 'vue';
import { getSportLiveTvSelectionEventsPage } from '@/api/sport-live-tv';
import { arrangeSportLiveTvEvents, uniqueSportLiveTvEvents } from '@/sport-live-tv/helpers';

export const useSportLiveTvSelectionEvents = ({ selection, status, sort }) => {
  const loadedEvents = ref([]);
  const nextOffset = ref(0);
  const hasNextPage = ref(false);
  const isLoading = ref(false);
  const isFetchingNextPage = ref(false);
  const isError = ref(false);
  let requestId = 0;

  const isEmptySelection = (value) => !value.leagues.length && !value.teams.length;

  const loadPage = async (loadingState, previousEvents, offset) => {
    const currentRequestId = ++requestId;
    loadingState.value = true;
    isError.value = false;

    try {
      const page = await getSportLiveTvSelectionEventsPage(toValue(selection), toValue(status), previousEvents, offset);
      if (currentRequestId !== requestId) return;

      loadedEvents.value = page.events;
      hasNextPage.value = page.hasNextPage;
      nextOffset.value = page.nextOffset;
    } catch {
      if (currentRequestId === requestId) isError.value = true;
    } finally {
      if (currentRequestId === requestId) loadingState.value = false;
    }
  };

  const reload = () => {
    loadedEvents.value = [];
    nextOffset.value = 0;
    hasNextPage.value = false;
    isFetchingNextPage.value = false;

    if (isEmptySelection(toValue(selection))) {
      requestId++;
      isLoading.value = false;
      return;
    }

    loadPage(isLoading, [], 0);
  };

  const fetchNextPage = () => loadPage(isFetchingNextPage, loadedEvents.value, nextOffset.value);

  watch([() => toValue(selection), () => toValue(status)], reload, { immediate: true });

  const events = computed(() => arrangeSportLiveTvEvents(uniqueSportLiveTvEvents(loadedEvents.value), toValue(status), toValue(sort)));

  return { events, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage, reload };
};
